import { Injectable } from '@angular/core';
import Pusher, { Channel } from 'pusher-js';
import { finalize, Observable, Subject, takeUntil, tap } from 'rxjs';
import { PushNotificationConfigService } from './push-notification-config.service';

export interface PusherEvent<T> {
  event: string;
  data: T;
}

@Injectable({ providedIn: 'root' })
export class PushNotificationService {
  constructor(private readonly pushNotificationConfigService: PushNotificationConfigService) {
    this.pushNotificationConfigService.token$
      .pipe(
        // Reconnect Pusher with new token whenever it changes
        tap(() => this.connect()),
      )
      .subscribe();
  }

  private pusherJs: Pusher | undefined;
  private isConnected = false;
  private readonly channels: Record<
    string,
    {
      channel: Channel;
      destroy$$: Subject<void>;
      events: Record<string, Subject<unknown>>;
    }
  > = {};

  get pusher(): Pusher | undefined {
    return this.pusherJs;
  }

  /**
   * Open a Pusher channel.
   *
   * Returns an object that is both an
   * - Observable, that emits all events of the given channel
   * - and contains a function `listen`, which can be subscribed to to listen for specific events on the `Channel`.
   */
  public openChannel<T>(
    channelName: string,
    options?: { skipSetPrefix?: boolean },
  ): Observable<PusherEvent<T>> & {
    listen: <U = T>(eventName: string) => Observable<U>;
  } {
    let { skipSetPrefix } = options || {};

    // do not prefix "presence-" channels
    // https://pusher.com/docs/channels/using_channels/presence-channels/
    if (channelName.startsWith('presence-')) {
      skipSetPrefix = true;
    }

    if (!skipSetPrefix) {
      channelName = `private-${channelName}`;
    }

    if (!this.channels[channelName]) {
      const pusher = this.connect();
      const channel = pusher.subscribe(channelName);
      const channel$$ = this.channels[channelName]?.events[channelName] || new Subject<unknown>();
      const destroy$$ = this.channels[channelName]?.destroy$$ || new Subject<void>();

      this.channels[channelName] = {
        channel,
        destroy$$,
        events: { [channelName]: channel$$ },
      };

      this.listen(channelName);
    }

    const ret = this.channels[channelName]!.events[channelName]!.asObservable().pipe(
      takeUntil(this.channels[channelName]!.destroy$$),
      finalize<unknown>(() => this.close(channelName)),
    ) as Observable<PusherEvent<T>> & {
      listen: <U = T>(eventName: string) => Observable<U>;
    };

    ret.listen = <U>(eventName: string) => this.listen(channelName, eventName) as Observable<U>;

    return ret;
  }

  /**
   * Listen to specific event on `channelName`.
   * Returns an Observable that automatically unsubscribes when the channel is closed.
   * @param channelName
   * @returns
   */
  public listen<T>(channelName: string, eventName?: string) {
    const channel$$ = this.channels[channelName]!.events[eventName || channelName] || new Subject<unknown>();
    const destroy$$ = this.channels[channelName]!.destroy$$;
    const channel = this.channels[channelName]!.channel;

    if (!eventName) {
      channel.bind_global((event: string, data: T) => {
        if (event.match(/^pusher:/)) {
          // ignore (re-) connect events
          return;
        }

        console.debug(`[PushNotificationService]: "${channel.name}" fired`, data);
        channel$$.next({ event, data });
      });
    } else {
      channel.bind(eventName, (data: T) => {
        console.debug(`[PushNotificationService]: "${channel.name} - ${eventName}" fired`, data);
        channel$$.next(data);
      });
    }

    return channel$$.asObservable().pipe(takeUntil(destroy$$));
  }

  public close(channelName: string) {
    if (!channelName) {
      throw new Error(`[PushNotificationService]: must provide a channelName to close.`);
    }

    // do not prefix "presence-" channels
    // https://pusher.com/docs/channels/using_channels/presence-channels/
    channelName = channelName.startsWith('presence-') ? channelName : `private-${channelName}`;
    this.pusherJs?.unsubscribe(channelName);

    if (this.channels[channelName]) {
      this.channels[channelName]!.channel.unbind_all();
      this.channels[channelName]!.channel.unsubscribe();
      this.channels[channelName]!.destroy$$.next();
      this.channels[channelName]!.destroy$$.complete();

      Object.values(this.channels[channelName]?.events || {}).forEach((subject) => subject.complete());
      delete this.channels[channelName];
    }
  }

  private connect(): Pusher {
    if (this.isConnected && this.pusherJs) {
      this.pusherJs.config.auth = this.pusherAuthSettings;
      return this.pusherJs;
    }

    this.pusherJs = new Pusher(this.pushNotificationConfigService.pusherKey, {
      cluster: this.pushNotificationConfigService.pusherCluster,
      forceTLS: true,
      authEndpoint: this.pushNotificationConfigService.authUrl,
      auth: this.pusherAuthSettings,
    });
    this.isConnected = true;
    return this.pusherJs;
  }

  private get pusherAuthSettings() {
    return {
      headers: {
        Authorization: `Bearer ${this.pushNotificationConfigService.getAccessToken()}`,
      },
    };
  }
}
